import { memo, useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import LineDownloadAltIcon from 'LuxKit/Icons/line/LineDownloadAltIcon'
import LineFileCheckIcon from 'LuxKit/Icons/line/LineFileCheckIcon'
import LineFileDownloadIcon from 'LuxKit/Icons/line/LineFileDownloadIcon'
import { createGenericTourItineraryPDFUrl, downloadGenericTourItineraryPDFUrl } from 'api/tour/tourV2'
import { downloadPdf, redirectToPdf } from 'lib/web/downloadPdf'
import { hasCustomisedDownloadHandler, isAndroidAppUserAgent } from 'lib/web/deviceUtils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'

interface Props {
  offerId: string
  offerSlug: string
  tourOptionId: string
  region: string
}

function TourV2ItineraryPdfDownload({
  offerId,
  offerSlug,
  tourOptionId,
  region,
}: Props) {
  const intl = useIntl()
  const [isDownloading, setIsDownloading] = useState(false)
  const [isDownloadSuccess, setIsDownloadSuccess] = useState(false)

  const handleDownload = useCallback(async() => {
    setIsDownloading(true)
    try {
      const userAgent = navigator.userAgent || ''
      const isAndroidApp = isAndroidAppUserAgent(userAgent)
      const shouldUseNativeDownloadHandler = hasCustomisedDownloadHandler()

      if (isAndroidApp && !shouldUseNativeDownloadHandler) {
        // HACK: pass API PDF link directly for Android app as fallback
        // can only use Google Docs for rendering PDF under this case, which may not work for all customers
        redirectToPdf(createGenericTourItineraryPDFUrl(offerId, tourOptionId, region))
        setIsDownloadSuccess(true)
        showSnackbar(intl.formatMessage({
          id: 'XeKatQYi334O9xYlgKTr',
          defaultMessage: 'Preparing tour documentation PDF...',
          description: 'Snackbar message when preparing the tour documentation PDF download on the offer page',
        }), 'success')
      } else {
        const blob = await downloadGenericTourItineraryPDFUrl(offerId, tourOptionId, region)
        const truncatedSlug = offerSlug.split('-').filter(Boolean).slice(0, 5).join('-')
        const fileName = `tour-documentation-${truncatedSlug}-${region}-PDF.pdf`
        downloadPdf(blob, fileName)
        setIsDownloadSuccess(true)
        showSnackbar(intl.formatMessage({
          id: 'wbHXeuK7Ne4by7fPIa85',
          defaultMessage: 'Tour documentation PDF downloaded.',
          description: 'Snackbar message when the tour documentation PDF download succeeds on the offer page',
        }), 'success')
      }
    } catch {
      showSnackbar(intl.formatMessage({
        id: 'Nd_TT5V5bysWXvtL7bKf',
        defaultMessage: 'Failed to download tour documentation PDF.',
        description: 'Snackbar message when the tour documentation PDF download fails on the offer page',
      }), 'critical')
      setIsDownloadSuccess(false)
    } finally {
      setIsDownloading(false)
    }
  }, [offerId, offerSlug, region, tourOptionId, intl])

  const startIcon = isDownloading
    ? <LoadingSpinner />
    : isDownloadSuccess
      ? <LineFileCheckIcon />
      : <LineDownloadAltIcon />

  return (
    <Sheet border="light-foreground" borderRadius="M" padding={16}>
      <Group direction="vertical" gap={12}>
        <Group direction="horizontal" gap={12} verticalAlign="center">
          <LineFileDownloadIcon size={24} colour="primary" />
          <Group direction="vertical" gap={2}>
            <Heading variant="heading6">
              <FormattedMessage
                id="l0KVk6ThpDADLFz3Zj6L"
                defaultMessage="Preview this tour in one PDF"
                description="Heading introducing the downloadable tour documentation PDF on the offer page"
              />
            </Heading>
            <BodyText variant="medium" colour="light-foreground-alt">
              <FormattedMessage
                id="19QpmG3neYZ2h-S45Lmd"
                defaultMessage="A collation of your day-by-day itinerary and trip highlights."
                description="Description text for the downloadable tour documentation PDF on the offer page"
              />
            </BodyText>
          </Group>
        </Group>
        <TextButton
          kind="secondary"
          startIcon={startIcon}
          onClick={handleDownload}
          disabled={isDownloading}
          fit="full-width"
        >
          {intl.formatMessage({
            id: 'hgs1o9yn-hAY9DH4MmgR',
            defaultMessage: 'Download',
            description: 'Button label to download the tour documentation PDF from the tour offer page',
          })}
        </TextButton>
      </Group>
    </Sheet>
  )
}

export default memo(TourV2ItineraryPdfDownload)
